import { useEffect, useMemo, useState } from "react";
import useStore from "../../../../useStore";
import { fmtCurrency } from "../../../../Utilities/Moneda/moneda";
import { fmtDate } from "../../../../Utilities/Fecha/fecha";
import "./ReporteSemanal.css";

// selectedDate es un Date "de calendario" (mismo que usa CustomCalendar/react-calendar,
// ya en hora local del navegador) → lo formateamos con getters locales, sin tocar UTC.
const toDateInput = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtRangoCorto = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

// El reporte muestra la semana (lunes–domingo) que contiene `selectedDate`,
// el mismo día que ya controla el calendario de arriba.
export default function ReporteSemanal({ restauranteId, selectedDate }) {
  const { weeklySalesReport, loadingWeeklySalesReport, errorWeeklySalesReport, fetchDataWeeklySalesReport } = useStore();

  const [showCancelados, setShowCancelados] = useState(false);
  const [showAbandonados, setShowAbandonados] = useState(false);

  const refDate = useMemo(
    () => toDateInput(selectedDate instanceof Date ? selectedDate : new Date()),
    [selectedDate]
  );

  useEffect(() => {
    if (!restauranteId) return;
    fetchDataWeeklySalesReport(restauranteId, refDate);
  }, [restauranteId, refDate, fetchDataWeeklySalesReport]);

  const rango = weeklySalesReport?.rango;
  const rangoLabel = useMemo(() => {
    if (!rango) return "";
    return `${fmtRangoCorto(rango.inicio)} – ${fmtRangoCorto(rango.fin)}`;
  }, [rango]);

  return (
    <div className="rs-panel card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="rs-title mb-0">📊 Reporte semanal de ventas</h5>
          <span className="rs-rango" title="Semana del día seleccionado en el calendario">
            {rangoLabel || "…"}
          </span>
        </div>

        {loadingWeeklySalesReport && (
          <div className="rs-loading text-center py-4">Cargando reporte…</div>
        )}

        {!loadingWeeklySalesReport && !!errorWeeklySalesReport && (
          <div className="alert alert-danger">{errorWeeklySalesReport}</div>
        )}

        {!loadingWeeklySalesReport && !errorWeeklySalesReport && weeklySalesReport && (
          <>
            <div className="row g-3">
              <div className="col-6 col-md-3">
                <div className="rs-stat rs-stat--total">
                  <span className="rs-stat-label">Total vendido</span>
                  <span className="rs-stat-value">{fmtCurrency(weeklySalesReport.ventas?.total)}</span>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="rs-stat rs-stat--cash">
                  <span className="rs-stat-label">Efectivo</span>
                  <span className="rs-stat-value">{fmtCurrency(weeklySalesReport.ventas?.efectivo)}</span>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="rs-stat rs-stat--mp">
                  <span className="rs-stat-label">Mercado Pago</span>
                  <span className="rs-stat-value">{fmtCurrency(weeklySalesReport.ventas?.mercadoPago)}</span>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="rs-stat rs-stat--cancel">
                  <span className="rs-stat-label">Cancelado ({weeklySalesReport.cancelados?.cantidad || 0})</span>
                  <span className="rs-stat-value">{fmtCurrency(weeklySalesReport.cancelados?.monto)}</span>
                </div>
              </div>
            </div>

            {weeklySalesReport.cancelados?.cantidad > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setShowCancelados((v) => !v)}
                >
                  {showCancelados ? "Ocultar" : "Ver"} pedidos cancelados ({weeklySalesReport.cancelados.cantidad})
                </button>

                {showCancelados && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm rs-table">
                      <thead>
                        <tr>
                          <th>Pedido</th>
                          <th>Cliente</th>
                          <th>Método</th>
                          <th>Total</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklySalesReport.cancelados.pedidos.map((p) => (
                          <tr key={p.orderId}>
                            <td><code>{p.orderId}</code></td>
                            <td>{p.cliente || "-"}</td>
                            <td>{p.paymentMethod || "-"}</td>
                            <td>{fmtCurrency(p.total)}</td>
                            <td>{fmtDate(p.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {weeklySalesReport.abandonados?.cantidad > 0 && (
              <div className="mt-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setShowAbandonados((v) => !v)}
                >
                  {showAbandonados ? "Ocultar" : "Ver"} pedidos abandonados ({weeklySalesReport.abandonados.cantidad})
                </button>

                {showAbandonados && (
                  <div className="table-responsive mt-2">
                    <table className="table table-sm rs-table">
                      <thead>
                        <tr>
                          <th>Pedido</th>
                          <th>Cliente</th>
                          <th>Método</th>
                          <th>Total</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklySalesReport.abandonados.pedidos.map((p) => (
                          <tr key={p.orderId}>
                            <td><code>{p.orderId}</code></td>
                            <td>{p.cliente || "-"}</td>
                            <td>{p.paymentMethod || "-"}</td>
                            <td>{fmtCurrency(p.total)}</td>
                            <td>{fmtDate(p.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <p className="rs-footnote mt-3 mb-0">
              {weeklySalesReport.pedidosTotalesEnRango} pedido(s) en esta semana · El efectivo te lo entrega el repartidor (él lo cobra del cliente), Mercado Pago te lo paga el admin · No incluye envío ni propina (son del repartidor).
            </p>
          </>
        )}
      </div>
    </div>
  );
}
